import { useEffect, useState } from 'preact/hooks';
import { api, ApiErr, type Dataset, type EnrolmentRow, type ResultRow } from '@/lib/teaching/teacher-api';
import { useAssessmentId, invalidateDataset, refreshAssessments } from '@/lib/teaching/state';
import { TeachingPage, Card, AssessmentSwitcher } from '@/components/teaching/ui';
import { Btn, ErrorLine, val } from '@/components/teaching/form';
import { markText, type Queue } from '@/lib/teaching/slice2';

// Stopgap manual marks entry (spec §06, R5, R15). Each cell takes a mark,
// or b = blank (scores 0), u = unreadable, m = page missing (no evidence,
// not a zero). Empty = not marked yet. Saves one student row at a time.

interface Cell { text: string; evidence: string; codes: string[] }

function cellFrom(r: ResultRow | undefined): Cell {
  if (!r) return { text: '', evidence: '', codes: [] };
  const text = r.blank ? 'b' : r.mark !== null ? String(r.mark) : r.unavailable_reason === 'unreadable' ? 'u' : 'm';
  return { text, evidence: r.evidence ?? '', codes: r.codes };
}

function toPayload(itemId: string, c: Cell, had: boolean) {
  const t = c.text.trim().toLowerCase();
  if (!t) return had ? { itemId, unmark: true } : null;
  const base = { itemId, evidence: c.evidence || null, codes: c.codes };
  if (t === 'b') return { ...base, blank: true, mark: 0, codes: [] };
  if (t === 'u') return { ...base, mark: null, unavailableReason: 'unreadable', codes: [] };
  if (t === 'm') return { ...base, mark: null, unavailableReason: 'page missing', codes: [] };
  return { ...base, mark: /^\d+$/.test(t) ? Number(t) : NaN };
}

const ATTEND: EnrolmentRow['attendance'][] = ['present', 'absent', 'catchup', 'exempt'];
const ATTEND_LABEL: Record<string, string> = { present: 'Sat it', absent: 'Absent', catchup: 'Catch-up', exempt: 'Exempt' };

export function Marks() {
  const a = useAssessmentId();
  const [ds, setDs] = useState<Dataset | null>(null);
  const [cls, setCls] = useState('all');
  // Pending AI proposals show greyed in empty cells (slice 2 §10); approve them on Review.
  const [pending, setPending] = useState<Map<string, string>>(new Map());
  const load = async () => {
    if (!a) return;
    const [d, q] = await Promise.all([api<Dataset>('GET', `/api/t/assessments/${a}/dataset`), api<Queue>('GET', `/api/t/assessments/${a}/queue`)]);
    setDs(d);
    setPending(new Map(q.papers.flatMap((p) => p.cells.filter((c) => p.student_id && c.proposal?.status === 'pending')
      .map((c) => [`${p.student_id}|${c.item_id}`, markText(c.proposal)] as [string, string]))));
  };
  useEffect(() => { void load(); }, [a]);
  const changed = async () => { if (a) invalidateDataset(a); await load(); void refreshAssessments(); };

  if (a === null) return <TeachingPage title="Marks"><div class="text-[13px] text-[var(--color-text-muted)]">Create a test first.</div></TeachingPage>;
  if (!ds || !a) return <TeachingPage title="Marks"><div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div></TeachingPage>;

  const rows = ds.students.filter((s) => cls === 'all' || s.class_name_at_sitting === cls);
  return (
    <TeachingPage title="Marks" breadcrumb={`${ds.assessment.title} · Year ${ds.assessment.year_level}`}
      actions={<>
        <AssessmentSwitcher current={a} />
        <select aria-label="Class" class="h-9 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-2 text-[12.5px] text-[var(--color-text)]" value={cls} onChange={(e) => setCls(val(e))}>
          <option value="all">All classes</option>
          {ds.classes.map((c) => <option key={c}>{c}</option>)}
        </select>
      </>}>
      <div class="text-[12px] text-[var(--color-text-muted)]">
        Type a mark, or <b>b</b> blank (scores 0), <b>u</b> unreadable, <b>m</b> page missing (no evidence, not a zero). Leave empty if not marked yet. Tick "Paper checked" before entering marks.
      </div>
      {ds.items.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">This test has no questions yet.</div></Card>}
      {ds.items.length > 0 && (
        <Card class="!p-0 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="text-[12.5px] border-collapse">
              <thead>
                <tr class="text-left text-[11px] uppercase tracking-wider text-[var(--color-text-faint)] bg-[var(--color-elevated)]">
                  <th class="p-2 sticky left-0 bg-[var(--color-elevated)] z-10 min-w-[160px]">Student</th>
                  <th class="p-2">Attendance</th>
                  <th class="p-2">Paper checked</th>
                  {ds.items.map((it) => <th key={it.id} class="p-2 text-center" title={it.prompt}>{it.label}<div class="font-normal normal-case">/{it.max_mark}</div></th>)}
                  <th class="p-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => <MarkRow key={s.student_id + s.revision} ds={ds} s={s} pending={pending} onChanged={changed} />)}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </TeachingPage>
  );
}

function MarkRow({ ds, s, pending, onChanged }: { ds: Dataset; s: EnrolmentRow; pending: Map<string, string>; onChanged: () => Promise<void> }) {
  const a = ds.assessment.id;
  const existing = new Map(ds.results.filter((r) => r.student_id === s.student_id).map((r) => [r.item_id, r]));
  const init = () => Object.fromEntries(ds.items.map((it) => [it.id, cellFrom(existing.get(it.id))]));
  const [cells, setCells] = useState<Record<string, Cell>>(init);
  const [err, setErr] = useState<ApiErr | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const present = s.attendance === 'present';
  const verified = s.identity_status === 'verified';
  const editable = present && verified;
  const dirty = ds.items.some((it) => JSON.stringify(cells[it.id]) !== JSON.stringify(cellFrom(existing.get(it.id))));

  async function sitting(body: Record<string, unknown>) {
    setErr(null);
    if (body.attendance && body.attendance !== 'present' && existing.size && !confirm(`Mark ${s.name} as ${ATTEND_LABEL[body.attendance as string].toLowerCase()}? Their ${existing.size} marks will be voided (kept in the log).`)) return;
    try { await api('PUT', `/api/t/assessments/${a}/sittings/${s.student_id}`, body, s.revision); await onChanged(); }
    catch (x) { setErr(x as ApiErr); if ((x as ApiErr).status === 409) await onChanged(); }
  }

  async function save() {
    setErr(null); setSaving(true);
    const results = ds.items.map((it) => toPayload(it.id, cells[it.id], existing.has(it.id))).filter(Boolean);
    try { await api('PUT', `/api/t/assessments/${a}/results/${s.student_id}`, { results }, s.revision); await onChanged(); }
    catch (x) { setErr(x as ApiErr); setSaving(false); if ((x as ApiErr).status === 409) await onChanged(); }
  }

  const fieldErr = (itemId: string) => err?.fields && Object.entries(err.fields).find(([k]) => k.startsWith(`results.${itemId}`))?.[1];
  const setCell = (id: string, patch: Partial<Cell>) => setCells({ ...cells, [id]: { ...cells[id], ...patch } });

  return (
    <>
      <tr class="border-t border-[var(--color-border)]" data-student={s.name}>
        <td class="p-2 sticky left-0 bg-[var(--color-card)] z-10">
          <div class="text-[13px] text-[var(--color-text)] whitespace-nowrap">{s.name}</div>
          <div class="text-[11px] text-[var(--color-text-faint)]">{s.class_name_at_sitting}</div>
        </td>
        <td class="p-2">
          <select aria-label={`Attendance ${s.name}`} value={s.attendance} onChange={(e) => sitting({ attendance: val(e) })}
            class="h-8 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 text-[12px] text-[var(--color-text)]">
            {ATTEND.map((x) => <option key={x} value={x}>{ATTEND_LABEL[x]}</option>)}
          </select>
        </td>
        <td class="p-2 text-center">
          <input type="checkbox" aria-label={`Paper checked ${s.name}`} title="This paper is this student's" checked={verified} disabled={!present}
            onChange={() => sitting({ identity_verified: !verified })} />
        </td>
        {ds.items.map((it) => {
          const c = cells[it.id];
          const fe = fieldErr(it.id);
          const ai = pending.get(`${s.student_id}|${it.id}`);
          return (
            <td key={it.id} class="p-1 text-center">
              <div class="flex items-center gap-0.5">
                <input aria-label={`${s.name} ${it.label}`} disabled={!editable} value={c.text} onInput={(e) => setCell(it.id, { text: val(e) })}
                  class={'w-10 h-8 rounded-md border text-center text-[13px] bg-[var(--color-bg)] text-[var(--color-text)] disabled:opacity-40 ' + (fe ? 'border-[#b42318]' : 'border-[var(--color-border)]')}
                  placeholder={ai} title={fe ?? (ai ? `AI suggests ${ai}: approve it on the Review queue` : undefined)} />
                <button type="button" disabled={!editable} onClick={() => setOpen(open === it.id ? null : it.id)} aria-label={`Codes ${s.name} ${it.label}`}
                  class={'w-5 h-8 text-[11px] rounded ' + (c.codes.length ? 'text-[#b42318] font-bold' : 'text-[var(--color-text-faint)]')}>{c.codes.length || '+'}</button>
              </div>
            </td>
          );
        })}
        <td class="p-2">{editable && <Btn kind="primary" disabled={!dirty || saving} onClick={save}>Save</Btn>}</td>
      </tr>
      {(open || err) && (
        <tr><td colSpan={ds.items.length + 4} class="px-2 pb-2">
          {err && <ErrorLine error={`${err.error}${err.fields ? ': ' + Object.values(err.fields).join(' · ') : ''}`} />}
          {open && <CodeEditor ds={ds} itemId={open} cell={cells[open]} onChange={(p) => setCell(open, p)} onClose={() => setOpen(null)} />}
        </td></tr>
      )}
    </>
  );
}

function CodeEditor({ ds, itemId, cell, onChange, onClose }: { ds: Dataset; itemId: string; cell: Cell; onChange: (p: Partial<Cell>) => void; onClose: () => void }) {
  const item = ds.items.find((i) => i.id === itemId)!;
  const linked = ds.misconceptions.filter((m) => m.item_ids.includes(itemId));
  return (
    <div class="rounded-xl border border-[var(--color-border)] bg-[var(--color-elevated)] p-3 flex flex-col gap-2 max-w-[640px]">
      <div class="text-[12.5px] text-[var(--color-text)]"><b>{item.label}</b> {item.prompt}</div>
      <textarea aria-label="Evidence excerpt" rows={2} value={cell.evidence} onInput={(e) => onChange({ evidence: val(e) })}
        placeholder="Quote the part of the answer that shows the misconception"
        class="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-2 text-[12.5px] text-[var(--color-text)]" />
      <div class="flex flex-wrap gap-1.5">
        {linked.length === 0 && <span class="text-[12px] text-[var(--color-text-muted)]">No codes are linked to this question.</span>}
        {linked.map((m) => {
          const on = cell.codes.includes(m.id);
          return (
            <label key={m.id} class={'px-2 py-1 rounded-lg border text-[12px] cursor-pointer ' + (on ? 'border-[#b42318] bg-[#fde2e2]' : 'border-[var(--color-border)] bg-[var(--color-card)]')} style={on ? { color: '#1a1a1a' } : undefined}>
              <input type="checkbox" class="mr-1" checked={on} onChange={() => onChange({ codes: on ? cell.codes.filter((x) => x !== m.id) : [...cell.codes, m.id] })} />
              <b>{m.code}</b> {m.meaning}
            </label>
          );
        })}
      </div>
      <div><Btn onClick={onClose}>Done</Btn></div>
    </div>
  );
}
